/**
 * @file Hypervisor-neutral HTTP route contracts for
 *       `@labcraft/hypervisor`. The `kind` field on
 *       `BootMachineRequest` selects the backend (today only
 *       `firecracker` is implemented; `qemu`/`virtualbox` slots
 *       reserved for future).
 * @module @labcraft/hypervisor/shared/routes
 *
 * The hypervisor daemon binds 127.0.0.1 in dev (no auth) and may grow
 * mTLS in production.
 *
 * Paths are wire-true: each `.path` is the full URL the daemon serves
 * at. The `bindRoute()` enforcement requires every path to start with
 * `/${serviceId}`; the daemon `id` is `'hypervisor'`, so every route
 * here starts with `/hypervisor`.
 */

import { z } from 'zod'
import { defineRoute, CoreString } from '@labcraft/core'

////////////////////////////////////////////////////////////////////////////////
// Value objects
////////////////////////////////////////////////////////////////////////////////

/**
 * Hypervisor backend kind — which runtime actually serves a Machine.
 * `firecracker` is the real impl; `fakemachine` is the KVM-free in-process
 * test double (umbrella #1048 P3 — it reports its true identity now rather
 * than masquerading as `firecracker`); `qemu` / `virtualbox` are slots
 * reserved for future providers. On a boot request `kind` is the author's
 * *desired* backend; the deploy decides which provider is registered and
 * that provider serves the boot regardless (the requester doesn't route —
 * see `MachineProvider.pick`), so a Machine's `kind` in a response is
 * descriptive telemetry: the backend that truly served it.
 */
export const HypervisorKindSchema = z.enum([
  'firecracker',
  'fakemachine',
  'qemu',
  'virtualbox',
])

/** Parsed `HypervisorKindSchema`. */
export type HypervisorKind = z.infer<typeof HypervisorKindSchema>

/**
 * Lifecycle state exposed to learners through the SPA. Eleven explicit
 * states plus implicit `null` (no frame emitted) cover every observable
 * VM transition. The vocabulary is 1:1 between the wire (this schema)
 * and the internal `Firecracker` machinery after umbrella #824
 * Phase 3 rewires the internals; this phase establishes the wire shape.
 *
 * Lives here (the machine value-object module) rather than in
 * `subscriptions.ts` so the `MachineSchema.currentLifecycleState` field
 * (umbrella #1258 Part H / #1337) can reference it without a
 * `routes.ts ↔ subscriptions.ts` import cycle. `subscriptions.ts`
 * re-exports it, so `@labcraft/hypervisor/shared/subscriptions` consumers
 * (lab, admin) import it from the same path as before.
 *
 * Boot path:
 * - `provisioning`: host-side resources being allocated (network /30,
 *   jail dir, cgroup).
 * - `provisioned`: host-side resources allocated; FC binary not yet
 *   spawned.
 * - `starting`: jailer+firecracker spawn issued; FC process exists but
 *   no ready signal yet.
 * - `started`: FC process is up, awaiting the in-guest
 *   `labcraft-agent` ready signal on vsock 8124.
 * - `running`: VM interactive — pty bytes flow, learner sees a prompt.
 *
 * Pause / snapshot path (chapter-start snapshot):
 * - `pausing`: FC `PATCH /vm {state: Paused}` issued.
 * - `paused`: FC reports Paused; snapshot write is next.
 * - `snapshotting`: `PUT /snapshot/create` in flight, writing
 *   `snapshot.bin` + `memory.bin` to the jail dir.
 * - `running`: terminal returned from the chapter-start path (FC keeps
 *   running through pause → write → resume).
 *
 * Reclaim path (disconnect / idle / Pause / TTL / operator-suspend):
 * - `terminating`: shutdown in progress (FC SIGTERM → SIGKILL → jail
 *   dir cleanup → network release). Transient before `null`.
 * - implicit `null` (no frame): VM is gone. The WS just closes.
 *
 * Terminal failure (#1009):
 * - `failed`: a snapshot write hung past the watchdog deadline and the
 *   machine is un-attachable and un-resumable. Sticky (survives until an
 *   explicit terminate) so the SPA can offer "start a fresh shell"
 *   rather than looping on the un-passable attach guard.
 *
 * Resume path (WS open on a saved VM):
 * - `saved`: FC process gone, chapter-start snapshot files on disk,
 *   resumable.
 * - `resuming`: `PUT /snapshot/load` in flight on a freshly-spawned FC.
 * - `running`: VM interactive again.
 *
 * The SPA's render vocabulary stays coarser than the wire vocabulary —
 * `provisioning / provisioned / starting / started` all render as the
 * boot spinner; `pausing / paused / snapshotting` render as the
 * "Pausing your lab…" dim — and is the responsibility of
 * `TerminalOverlay` and friends.
 */
export const MachineLifecycleStateSchema = z.enum([
  'provisioning',
  'provisioned',
  'starting',
  'started',
  'running',
  'pausing',
  'paused',
  'snapshotting',
  'saved',
  'resuming',
  'terminating',
  'failed',
])

/** Parsed `MachineLifecycleState`. */
export type MachineLifecycleState = z.infer<typeof MachineLifecycleStateSchema>

/**
 * Trigger that caused a lifecycle transition (#1056). Carried on the
 * lifecycle/close frames so the SPA can map `(machine, reason)` to a
 * specific recovery instead of collapsing every close into one
 * page-level eviction. The hypervisor stamps `idle-suspend` /
 * `voluntary-pause` / `daemon-restart` / `crash` / `operator-suspend` /
 * `content-refreshed` on the transitions it drives, and — since #1336 —
 * `budget-exhausted` on the reclaim ladder too: both the owner's own
 * `maxRunningMs` deadline firing and the lab-gate `budget-suspend`
 * command (which reaps a still-running VM whose learner is over the
 * daily cap) run the same reclaim path and stamp this reason. The lab
 * still additionally mints a bare `budget-exhausted` SPA frame when it
 * refuses a WS-open outright (no VM booted), and `engagement-expired`
 * on its own TTL-lapse frame — the enum is the shared vocabulary both
 * ends speak, so a reason is always a known member regardless of which
 * side stamped it.
 *
 * - `idle-suspend`: reclaimed after idle / a disconnect grace period.
 * - `voluntary-pause`: the learner clicked Pause.
 * - `operator-suspend`: an operator suspended the lab from the admin
 *   control plane (umbrella #1258 Part H / #1337). Distinct from
 *   `voluntary-pause` so the learner overlay reads "an operator paused
 *   your lab," not "you paused it." Drives the SAME reclaim path
 *   (running → pausing → paused → snapshotting → saved) as every other
 *   suspend — only the stamped reason differs.
 * - `daemon-restart`: the hypervisor daemon stopped (snapshot-on-stop).
 * - `crash`: the guest's pty exited unexpectedly.
 * - `budget-exhausted`: lab's daily VM-budget cap (#834).
 * - `engagement-expired`: the learner's engagement TTL lapsed.
 * - `content-refreshed`: the safety-net fresh-boot (umbrella #1258 Part D
 *   / #1333). A saved session's pinned overlay layers are no longer
 *   available (force-expired / pruned — e.g. a security base bump,
 *   #1335), so its snapshot can't be restored against the pin. The
 *   hypervisor discards it and cold-boots a fresh VM on the CURRENT
 *   content under the same id, stamping this reason on the `terminating`
 *   frame so the lab shows an explicit "your saved session was on older
 *   content — we've moved you to the current version" message instead of
 *   a silent scrollback loss or the dozed-off reconnect loop. Distinct
 *   from a content upgrade under an in-flight learner, which does NOT
 *   fresh-boot: the per-engagement pin holds and the snapshot restores
 *   cleanly.
 */
export const MachineLifecycleReasonSchema = z.enum([
  'idle-suspend',
  'voluntary-pause',
  'operator-suspend',
  'daemon-restart',
  'crash',
  'budget-exhausted',
  'engagement-expired',
  'content-refreshed',
])

/** Parsed `MachineLifecycleReason`. */
export type MachineLifecycleReason = z.infer<
  typeof MachineLifecycleReasonSchema
>

/**
 * The engagement's overlay-stack pin as the fleet read surfaces it
 * (umbrella #1258 Part D / #1333 + Part H / #1340) — the ordered layer
 * stack a running/saved engagement is welded to, plus a `stale` flag the
 * admin control plane reads to know a resume will clean-refresh instead
 * of restore. Populated from the owner's live `Firecracker.pinFor(id)`
 * so the Hypervisors panel can show each VM's pinned content version and
 * flag pins that will force-expire on their next resume.
 *
 * @property layers - Ordered overlay layer refs the engagement pinned,
 *   base-first (`layers[0]` is the base floor; the trailing entries are
 *   the pinned content layers). The panel renders the trailing content
 *   ref as the "pinned content version".
 * @property stackFingerprint - Fingerprint over the ordered stack at
 *   first boot; `null` when no layer ships a `SHA256SUMS` (dev dirs).
 * @property stale - Whether the pin is force-expired / security-expired —
 *   its next resume routes to the #1333 `content-refreshed` clean-refresh
 *   (a security base bump #1335, or an operator force-expire #1340) rather
 *   than a snapshot restore. A live, resumable pin is `false`.
 */
export const MachinePinSchema = z.object({
  layers: z.array(z.string().min(1)),
  stackFingerprint: z.string().nullable(),
  stale: z.boolean(),
})

/** Parsed `MachinePin`. */
export type MachinePin = z.infer<typeof MachinePinSchema>

/**
 * A Machine's live budget running-window usage (#1055 / #1575). Present on
 * the read-model once a running-window is open; a Machine with no open
 * window omits it. This is the same `{windowId, runningMs}` shape the
 * `lifecycle` frame already carries — lifted to a named schema so the
 * read-model (`MachineSchema.usage`) and the frame share one source of
 * truth and the ~60s usage sweep (#1575 Part 2) can upsert it into the
 * `vm_usage_windows` ledger keyed on `(vmId, windowId)`.
 *
 * @property windowId - The open running-window's id (#1055). Stable across
 *   a running period, so repeated reads and the ledger upsert are
 *   idempotent; a fresh window (a resume, or the UTC-midnight roll) mints
 *   a new id.
 * @property runningMs - Accumulated running ms for the current window. Live
 *   while the VM runs (grows across successive reads); frozen at the final
 *   value once the window closes (a `saved`/`paused` VM reports the frozen
 *   `windowMs`).
 */
export const MachineUsageSchema = z.object({
  windowId: z.string().min(1),
  runningMs: z.number().int().nonnegative(),
})

/** Parsed `MachineUsage`. */
export type MachineUsage = z.infer<typeof MachineUsageSchema>

/**
 * Externally-visible state of one booted Machine. Returned from `boot`,
 * `inspect`, and `list`. Hypervisor-neutral — every backend exposes
 * this shape regardless of how its underlying runtime tracks state.
 *
 * @property id - Opaque machineId. Caller-supplied at boot.
 * @property kind - Which backend actually served this Machine —
 *   descriptive telemetry, not the requested kind (`'fakemachine'` on the
 *   KVM-free test tier even when the boot recipe asked for `'firecracker'`).
 * @property name - Synthesized host-visible short name (jail dir
 *   basename for firecracker; analogous for other backends). Derived
 *   from `id` via the backend's `nameFor` so callers can recover the
 *   identifier on-host without round-tripping.
 * @property hostIp - Host TAP address (the Machine's default gateway).
 * @property guestIp - Machine-side address.
 * @property tap - Host-side TAP interface name.
 * @property ptyId - Opaque id naming the pty slot to dial for terminal
 *   I/O via the `/hypervisor/{id}/pty` subscription.
 * @property attached - True iff the backing runtime process has spawned.
 *   False between a successful `boot` and the first WS pty attach.
 * @property state - The Machine's current wire lifecycle state — the
 *   owner's live `MachineProvider.currentLifecycleState()` (umbrella
 *   #1258 Part H / #1337). Optional so a `Machine` value constructed
 *   outside a live session (a fixture, a `terminated` session mid-drain)
 *   omits it; every provider's `list()` populates it from the owning
 *   session so the admin control-plane read carries real per-machine
 *   state (the fix for the "0 running / N" filter, #1082). The wire uses
 *   `state` here (not `currentLifecycleState`) to match the established
 *   lifecycle-frame vocabulary (`PtyLifecycleFrame.state`) and the
 *   admin panel's reserved `AdminMachine.state`, which fills in via the
 *   proxy's structural pass-through with no admin wire change.
 * @property pin - The engagement's live overlay-stack pin (umbrella #1258
 *   Part D / #1333 + Part H / #1340), populated from the owner's
 *   `Firecracker.pinFor(id)`. Optional — only a backend that models pins
 *   (Firecracker) with a live/saved engagement carries one; the KVM-free
 *   fake tier and pin-less sessions omit it. The admin control plane reads
 *   it to surface each VM's pinned content version + `stale` flag.
 * @property usage - The live budget running-window usage (#1575 Part 1):
 *   the open window's id + accumulated running ms, from the owner's
 *   `windowUsage(session)`. Optional — a session with no open window (never
 *   ran, or between running periods) omits it. This is what the lab's ~60s
 *   sweep (#1575 Part 2) reads off `list()` to keep the `vm_usage_windows`
 *   ledger continuously true without a terminal socket attached.
 */
export const MachineSchema = z.object({
  id: z.string().min(1),
  kind: HypervisorKindSchema,
  name: z.string().min(1),
  hostIp: z.string().min(1),
  guestIp: z.string().min(1),
  tap: z.string().min(1),
  ptyId: z.string().min(1),
  attached: z.boolean(),
  // Live WS-attach count from the Pty bridge (#512 / #1051). Optional:
  // providers don't populate it — the daemon's list handler enriches
  // rows with the bridge-side count on the way out.
  attachedCount: z.number().int().nonnegative().optional(),
  state: MachineLifecycleStateSchema.optional(),
  pin: MachinePinSchema.optional(),
  usage: MachineUsageSchema.optional(),
})

/** Parsed `MachineSchema`. */
export type Machine = z.infer<typeof MachineSchema>

////////////////////////////////////////////////////////////////////////////////
// POST /hypervisor — boot a Machine
////////////////////////////////////////////////////////////////////////////////

/**
 * Recorded ownership metadata for a booted Machine (umbrella #1048 /
 * #1054). The caller (lab) supplies the semantic components of the
 * opaque `id`; the hypervisor records them as session metadata (and
 * into the durable manifest, #1053) so ownership is inspectable and
 * the durable record carries who/what a jail belongs to. Optional on
 * the wire — the `FakeMachine` tier and operator-issued boots may omit
 * them.
 *
 * @property owner - The learner (or tenant) the Machine belongs to.
 * @property name - The author-chosen machine slot (`'shell'`,
 *   `'client'`) the browser addresses.
 * @property specHash - Content hash of the `MachineSpec` that produced
 *   the id — the recomputability key (ADR-012 §4).
 */
export const MachineOwnershipSchema = z.object({
  owner: z.string().min(1),
  name: z.string().min(1),
  specHash: z.string().min(1),
})

/** Parsed `MachineOwnership`. */
export type MachineOwnership = z.infer<typeof MachineOwnershipSchema>

/**
 * Body of `POST /hypervisor`.
 *
 * @property id - Opaque machineId. Caller picks it.
 * @property kind - The author's *desired* backend. Advisory for routing:
 *   the deploy's registered provider serves the boot regardless (see
 *   `MachineProvider.pick`), so a fake-tier deploy serves a `'firecracker'`
 *   request and the response Machine reports `'fakemachine'`.
 * @property ownership - Optional recorded ownership metadata (#1054).
 * @property network - Optional per-Machine network policy overrides.
 *   `forbiddenSubnets` (when present) extends the daemon-level
 *   forbidden list. Future fields land here as needs surface.
 * @property base - Optional per-Part base-layer override (#1332). The
 *   layer-id of the base floor this VM stacks on; the hypervisor selects
 *   the staged base whose `layer.json` id matches AND is `kind === 'base'`
 *   (multiple bases — distro / version / date — may coexist). Absent →
 *   the platform default base (the hypervisor's `defaultBaseLayerId`).
 *   Threaded from the Part's `MachineSpec.base` by lab.
 * @property layers - Optional ADDITIONAL read-only overlay layers,
 *   stacked bottom-up above the base floor (ADR-032, umbrella
 *   #1256). The hypervisor always prepends its base floor — selected by
 *   id + role (`layer.json` `kind === 'base'`, #1332), not a hardcoded id
 *   — and appends a per-VM RW top, so these are only the extra content
 *   layers between them (do not include the base).
 *   Each id resolves to a staged layer under the hypervisor's
 *   `layerStoreDir`, mounted RO (`is_read_only: true`). Absent → the
 *   base floor alone.
 *   Threaded from the Part's `MachineSpec.layers` by lab (Part F #1266).
 */
export const BootMachineRequestSchema = z.object({
  id: z.string().min(1),
  kind: HypervisorKindSchema,
  ownership: MachineOwnershipSchema.optional(),
  base: z.string().min(1).optional(),
  /**
   * Remaining running-time budget in ms (#1055). The hypervisor arms a
   * per-session deadline for it and suspends on exhaustion. `null` /
   * absent = unlimited.
   */
  maxRunningMs: z.number().int().nonnegative().nullable().optional(),
  network: z
    .object({
      forbiddenSubnets: z.array(z.string()).optional(),
    })
    .optional(),
  layers: z.array(z.string().min(1)).nonempty().optional(),
})

/** Parsed `BootMachineRequest`. */
export type BootMachineRequest = z.infer<typeof BootMachineRequestSchema>

/**
 * The four host resource pools capacity accounts for (umbrella #1257 Part A,
 * #1280). The wire vocabulary shared by the boot response's at-capacity result
 * and (Part B, #1281) the availability route; the service-side `Capacity`
 * derives its `PoolName` from this enum so the two can't drift.
 */
export const CapacityPoolNameSchema = z.enum([
  'vcpus',
  'memoryMib',
  'diskMib',
  'networkSlots',
])

/** Parsed `CapacityPoolNameSchema`. */
export type CapacityPoolName = z.infer<typeof CapacityPoolNameSchema>

/**
 * Typed at-capacity result — the boot response variant returned when the host
 * lacks headroom, replacing the pre-#1257 reactive `MACHINE_NETWORK_EXHAUSTED`
 * deep throw (umbrella #1257 Part A, #1280). Lab (Part D, #1283) branches on
 * the `atCapacity` discriminant to enqueue the learner instead of failing.
 *
 * @property atCapacity - Always `true` (discriminant against a booted machine).
 * @property shortPools - The pools that lacked headroom, so the queue +
 *   observability can attribute the refusal.
 */
export const AtCapacityResultSchema = z.object({
  atCapacity: z.literal(true),
  shortPools: z.array(CapacityPoolNameSchema),
})

/** Parsed `AtCapacityResultSchema`. */
export type AtCapacityResult = z.infer<typeof AtCapacityResultSchema>

/**
 * One host pool's headroom snapshot (umbrella #1257 Part B, #1281).
 *
 * @property capacity - Total budgeted units.
 * @property used - Currently reserved units (running + held).
 * @property free - `capacity - used`, clamped at zero.
 */
export const PoolSnapshotSchema = z.object({
  capacity: z.number(),
  used: z.number(),
  free: z.number(),
})

/** Parsed `PoolSnapshotSchema`. */
export type PoolSnapshot = z.infer<typeof PoolSnapshotSchema>

/**
 * Host availability snapshot (umbrella #1257 Part B, #1281) — every pool's
 * headroom plus the derived `atCapacity` flag. Returned by the availability
 * query route and pushed on the availability subscription; lab's queue (Part D)
 * consumes it to decide when to admit its head. The service-side `Capacity`
 * derives its `CapacityAvailability` from this schema so the two can't drift.
 *
 * @property atCapacity - Whether a further minimal boot would be refused.
 * @property pools - Per-pool headroom, keyed by {@link CapacityPoolName}.
 */
export const CapacityAvailabilitySchema = z.object({
  atCapacity: z.boolean(),
  pools: z.object({
    vcpus: PoolSnapshotSchema,
    memoryMib: PoolSnapshotSchema,
    diskMib: PoolSnapshotSchema,
    networkSlots: PoolSnapshotSchema,
  }),
})

/** Parsed `CapacityAvailabilitySchema`. */
export type CapacityAvailability = z.infer<typeof CapacityAvailabilitySchema>

/**
 * Response of `POST /hypervisor`. Either the booted `machine` (`running` per
 * #832: `boot()` provisions, spawns firecracker, and waits for the in-guest
 * agent's ready signal before resolving), or a typed {@link AtCapacityResult}
 * when the host is full (umbrella #1257 Part A, #1280) — the discriminated
 * union the caller branches on rather than catching a deep throw.
 */
export const BootMachineResponseSchema = z.union([
  z.object({ machine: MachineSchema }),
  AtCapacityResultSchema,
])

/** Parsed `BootMachineResponse`. */
export type BootMachineResponse = z.infer<typeof BootMachineResponseSchema>

/**
 * `POST /hypervisor` — boot a Machine all the way to `running`.
 *
 * **Operator/admin use only (#832).** Lab no longer calls this — it
 * drives boot lazily via the terminal WS `open` frame (`PtySub`), so
 * the VM is born exactly when a learner opens its terminal. This route
 * survives for operator tooling (manual boot,
 * smoke tests, admin console) that wants to pre-warm a Machine without
 * a WS consumer.
 */
export const BootMachineRoute = defineRoute({
  method: 'POST',
  path: '/hypervisor',
  request: BootMachineRequestSchema,
  response: BootMachineResponseSchema,
  description: CoreString.oneLine`
    Operator/admin only: boot a Machine of the chosen kind to \`running\`
    (provision → spawn firecracker → await the in-guest ready signal). Lab
    drives boot via the terminal WS open frame, not this route.
  `,
})

////////////////////////////////////////////////////////////////////////////////
// DELETE /hypervisor/:id — shutdown
////////////////////////////////////////////////////////////////////////////////

/** `DELETE /hypervisor/:id` — graceful shutdown + jail/TAP cleanup. Idempotent. */
export const ShutdownMachineRoute = defineRoute({
  method: 'DELETE',
  path: '/hypervisor/:id',
  response: z.void(),
  description:
    'Graceful shutdown + jail/TAP cleanup. Idempotent — unknown ids are a no-op.',
})

////////////////////////////////////////////////////////////////////////////////
// POST /hypervisor/:id/suspend — voluntary suspend
////////////////////////////////////////////////////////////////////////////////

/** Path params for `POST /hypervisor/:id/suspend`. */
export const SuspendMachineParamsSchema = z.object({
  id: z.string().min(1),
})

/** Parsed `SuspendMachineParams`. */
export type SuspendMachineParams = z.infer<typeof SuspendMachineParamsSchema>

/**
 * `POST /hypervisor/:id/suspend` — voluntarily snapshot a running
 * Machine immediately (per-session live snapshot → `saved`). Bypasses
 * the post-detach grace period the scheduled snapshot waits out.
 *
 * The **operator-facing twin** of the SPA's in-band `{type:'suspend'}`
 * WS control frame (`PtySub`); both dispatch to the same provider
 * `suspend(id)`. Kept (not retired) so operator tooling can pause a VM
 * without holding a terminal WS open. Idempotent on already-suspended /
 * unknown ids.
 */
export const SuspendMachineRoute = defineRoute({
  method: 'POST',
  path: '/hypervisor/:id/suspend',
  response: z.void(),
  description: CoreString.oneLine`
    Voluntarily snapshot a running Machine immediately, bypassing the
    post-detach grace period. Idempotent on already-suspended / unknown ids.
  `,
})

////////////////////////////////////////////////////////////////////////////////
// POST /hypervisor/:id/operator-suspend — admin control-plane suspend
////////////////////////////////////////////////////////////////////////////////

/** Path params for `POST /hypervisor/:id/operator-suspend`. */
export const OperatorSuspendMachineParamsSchema = z.object({
  id: z.string().min(1),
})

/** Parsed `OperatorSuspendMachineParams`. */
export type OperatorSuspendMachineParams = z.infer<
  typeof OperatorSuspendMachineParamsSchema
>

/**
 * `POST /hypervisor/:id/operator-suspend` — the admin control-plane
 * suspend command (umbrella #1258 Part H / #1337). Drives the SAME
 * reclaim path as the learner-facing `POST /hypervisor/:id/suspend`
 * (running → pausing → paused → snapshotting → saved), but stamps the
 * `operator-suspend` lifecycle reason so the learner overlay reads "an
 * operator paused your lab," not their own Pause.
 *
 * Distinct route (not a flag on `suspend`) because it belongs to a
 * different, bounded command set: the admin control-plane command set is
 * a strict subset of owner operations (core invariant 1). Resume/boot
 * are intentionally NOT exposed here — a suspended lab only resumes when
 * its owner (the learner) dials back in. Idempotent on already-suspended
 * / unknown ids, mirroring `suspend`.
 */
export const OperatorSuspendMachineRoute = defineRoute({
  method: 'POST',
  path: '/hypervisor/:id/operator-suspend',
  response: z.void(),
  description: CoreString.oneLine`
    Admin control-plane suspend: snapshot a running Machine immediately via the
    shared reclaim path, stamping the operator-suspend lifecycle reason.
    Idempotent on already-suspended / unknown ids.
  `,
})

////////////////////////////////////////////////////////////////////////////////
// POST /hypervisor/:id/budget-suspend — daily-budget reap
////////////////////////////////////////////////////////////////////////////////

/** Path params for `POST /hypervisor/:id/budget-suspend`. */
export const BudgetSuspendMachineParamsSchema = z.object({
  id: z.string().min(1),
})

/** Parsed `BudgetSuspendMachineParams`. */
export type BudgetSuspendMachineParams = z.infer<
  typeof BudgetSuspendMachineParamsSchema
>

/**
 * `POST /hypervisor/:id/budget-suspend` — the daily-VM-budget reap
 * (umbrella #1258 Part E / #1336). Lab calls this when its WS-open gate
 * refuses a learner who is over the daily cap: the pre-#1336 gate closed
 * the refused socket but left any STILL-RUNNING VM burning host RAM (live
 * repro 2026-07-24: `attachedCount: 2` after the wall). This drives the
 * SAME reclaim path as a learner Pause / the owner's own `maxRunningMs`
 * deadline (running → pausing → paused → snapshotting → saved), snapshotting
 * live state THEN killing firecracker so RAM frees — but stamps the
 * `budget-exhausted` lifecycle reason so any other attached tab sees the
 * out-of-time takeover, not a spurious "you paused it." The snapshot welds
 * to the engagement pin (#1333), so the learner's next resume (tomorrow /
 * after upgrading) restores mid-chapter.
 *
 * Distinct route (not a flag on `suspend`) for the same reason
 * operator-suspend is: the stamped reason drives distinct learner-facing
 * copy, and the caller/command set differs. Resume is intentionally NOT
 * exposed — a budget-reaped lab only resumes when its owner dials back in
 * with headroom. Idempotent on already-suspended / unknown ids, mirroring
 * `suspend`.
 */
export const BudgetSuspendMachineRoute = defineRoute({
  method: 'POST',
  path: '/hypervisor/:id/budget-suspend',
  response: z.void(),
  description: CoreString.oneLine`
    Daily-budget reap: snapshot a still-running Machine immediately via the
    shared reclaim path, stamping the budget-exhausted lifecycle reason so
    host RAM frees instead of a walled VM lingering. Idempotent on
    already-suspended / unknown ids.
  `,
})

////////////////////////////////////////////////////////////////////////////////
// POST /hypervisor/:id/force-expire-pin — admin control-plane pin force-expiry
////////////////////////////////////////////////////////////////////////////////

/**
 * `POST /hypervisor/:id/force-expire-pin` — the admin control-plane
 * pin-force-expiry command (umbrella #1258 Part H / #1340). Flags ONE
 * VM's engagement pin (#1333) unavailable so its NEXT resume routes to the
 * safety-net clean-refresh (`freshBootReplacing` → `content-refreshed`)
 * instead of restoring the snapshot — the operator-driven, single-VM twin
 * of the automatic security-base-bump force-expiry (#1335), which flags a
 * whole base. The learner's next dial-in lands on the CURRENT content with
 * the explicit "your saved session was on older content" UX, never a
 * silent loss.
 *
 * A bounded command in the admin subset (core invariant 1): it only ever
 * FLAGS a pin — it never resumes, boots, or forces a state hop. The owner
 * is the gate; the flag takes effect only through the owner's own
 * resume-time `pinAvailable` check. Idempotent on already-flagged /
 * pin-less / unknown ids (no-op + 204), mirroring `suspend` /
 * `operator-suspend`.
 */
export const ForceExpirePinMachineRoute = defineRoute({
  method: 'POST',
  path: '/hypervisor/:id/force-expire-pin',
  response: z.void(),
  description: CoreString.oneLine`
    Admin control-plane pin force-expiry: flag one VM's engagement pin
    unavailable so its next resume clean-refreshes onto current content
    instead of restoring. Idempotent on already-flagged / unknown ids.
  `,
})

////////////////////////////////////////////////////////////////////////////////
// POST /hypervisor/:id/probe — out-of-band state probe (#1147 / #1149)
////////////////////////////////////////////////////////////////////////////////

/** Path params for `POST /hypervisor/:id/probe`. */
export const ProbeMachineParamsSchema = z.object({
  id: z.string().min(1),
})

/** Parsed `ProbeMachineParams`. */
export type ProbeMachineParams = z.infer<typeof ProbeMachineParamsSchema>

/**
 * Body of `POST /hypervisor/:id/probe`.
 *
 * @property check - Author-trusted shell script text the guest runs
 *   via `/bin/sh -c` (ADR-019 2026-07-11 amendment: course content
 *   from the rootfs trust domain — never learner input, never LLM
 *   output; side-effect-free, fast, idempotent reads only).
 * @property timeoutMs - Optional guest-side deadline; the daemon
 *   defaults it (5000 ms) when absent.
 * @property maxOutputBytes - Optional cap on the returned combined
 *   stdout+stderr tail; the daemon defaults it (4096) when absent.
 */
export const ProbeMachineRequestSchema = z.object({
  check: z.string().min(1),
  timeoutMs: z.number().int().positive().optional(),
  maxOutputBytes: z.number().int().positive().optional(),
})

/** Parsed `ProbeMachineRequest`. */
export type ProbeMachineRequest = z.infer<typeof ProbeMachineRequestSchema>

/**
 * Response of `POST /hypervisor/:id/probe` — the provider's
 * `ProbeResult` on the wire.
 *
 * @property exit - The probe's exit code, or `null` when it never
 *   exited on its own (timeout kill, signal death, spawn failure).
 * @property timedOut - Whether the guest-side timeout kill fired.
 * @property output - Last `maxOutputBytes` bytes of the probe's
 *   combined stdout+stderr (the tail — the end of a check's output is
 *   the part that says why it passed or failed).
 */
export const ProbeMachineResponseSchema = z.object({
  exit: z.number().int().nullable(),
  timedOut: z.boolean(),
  output: z.string(),
})

/** Parsed `ProbeMachineResponse`. */
export type ProbeMachineResponse = z.infer<typeof ProbeMachineResponseSchema>

/**
 * `POST /hypervisor/:id/probe` — run one author-supplied state-check
 * script inside the guest, out-of-band from the learner's pty
 * (#1147), and return its exit / timeout / bounded-output-tail
 * outcome. The consumer is the `{via:'state'}` detector path (#1149):
 * lab's terminal bridge relays browser `probe` frames here and maps
 * `exit === 0` to "satisfied". 404 when no registered provider knows
 * the id; 502 when the probe can't be delivered (agent channel down,
 * host backstop expiry).
 */
export const ProbeMachineRoute = defineRoute({
  method: 'POST',
  path: '/hypervisor/:id/probe',
  request: ProbeMachineRequestSchema,
  response: ProbeMachineResponseSchema,
  description: CoreString.oneLine`
    Run one author-trusted state-check script in the Machine's guest,
    out-of-band from the learner's pty, returning exit / timedOut / bounded
    output tail. Consumer: course-kit's state-goal probes (#1149).
  `,
})

////////////////////////////////////////////////////////////////////////////////
// GET /hypervisor — list
////////////////////////////////////////////////////////////////////////////////

/**
 * Response of `GET /hypervisor`. Snapshots every booted Machine at
 * call time.
 */
export const ListMachinesResponseSchema = z.object({
  machines: z.array(MachineSchema),
})

/** Parsed `ListMachinesResponse`. */
export type ListMachinesResponse = z.infer<typeof ListMachinesResponseSchema>

/** `GET /hypervisor` — list every booted Machine. */
export const ListMachinesRoute = defineRoute({
  method: 'GET',
  path: '/hypervisor',
  response: ListMachinesResponseSchema,
  description: 'List every booted Machine (operator ops view).',
})

////////////////////////////////////////////////////////////////////////////////
// GET /hypervisor/availability — host capacity headroom
////////////////////////////////////////////////////////////////////////////////

/**
 * `GET /hypervisor/availability` — current host {@link CapacityAvailability}
 * (umbrella #1257 Part B, #1281). Lab queries this at the boot gate to decide
 * whether to admit or enqueue; the paired `AvailabilitySub` streams changes.
 */
export const AvailabilityRoute = defineRoute({
  method: 'GET',
  path: '/hypervisor/availability',
  response: CapacityAvailabilitySchema,
  description: CoreString.oneLine`
    Current host capacity headroom across the four pools (vCPU / memory / disk /
    network-addresses). Lab's boot gate queries it; the availability WS streams
    changes on admit / reclaim / hold expiry.
  `,
})

////////////////////////////////////////////////////////////////////////////////
// POST /hypervisor/holds — hold a freed slot for a queued learner (#1257 Part D)
////////////////////////////////////////////////////////////////////////////////

/**
 * Request to hold a freed slot for a queued learner (umbrella #1257 Part D,
 * #1283). The hold is keyed by the `id` (vmId) the learner will boot, so the
 * claim is implicit: the subsequent boot of that id consumes the held
 * reservation instead of a fresh admit. The hypervisor supplies the cost from
 * its provider; the caller supplies only the window.
 *
 * @property id - The vmId the freed slot is held for (the queue's boot target).
 * @property ttlMs - The admission window in ms (lab's admissionTimeoutMs).
 */
export const HoldMachineRequestSchema = z.object({
  id: z.string().min(1),
  ttlMs: z.number().int().positive(),
})

/** Parsed `HoldMachineRequestSchema`. */
export type HoldMachineRequest = z.infer<typeof HoldMachineRequestSchema>

/**
 * Response of `POST /hypervisor/holds` — either the placed hold (with its
 * `expiresAt`) or a typed {@link AtCapacityResult} when even the freed slot no
 * longer has headroom (a race the queue re-offers around).
 *
 * @property held - Always `true` (discriminant against an at-capacity result).
 * @property expiresAt - Epoch ms the hold window closes.
 */
export const HoldMachineResponseSchema = z.union([
  z.object({ held: z.literal(true), expiresAt: z.number() }),
  AtCapacityResultSchema,
])

/** Parsed `HoldMachineResponseSchema`. */
export type HoldMachineResponse = z.infer<typeof HoldMachineResponseSchema>

/**
 * `POST /hypervisor/holds` — hold a freed slot for a queued learner's `id` for
 * `ttlMs` (umbrella #1257 Part D, #1283). The learner's next boot of that id
 * claims the hold implicitly; a miss lets it expire and the queue re-offers.
 */
export const HoldMachineRoute = defineRoute({
  method: 'POST',
  path: '/hypervisor/holds',
  request: HoldMachineRequestSchema,
  response: HoldMachineResponseSchema,
  description: CoreString.oneLine`
    Hold a freed slot for a queued learner's vmId for a bounded window; the
    learner's next boot of that id claims it implicitly. Returns the hold's
    expiry, or a typed at-capacity result if the slot was lost to a race.
  `,
})

////////////////////////////////////////////////////////////////////////////////
// GET /hypervisor/:id/cast — asciinema cast streaming
////////////////////////////////////////////////////////////////////////////////

/**
 * Path params for `GET /hypervisor/:id/cast`. `id` names the Machine
 * whose pty cast file the operator is streaming.
 */
export const StreamCastParamsSchema = z.object({
  id: z.string().min(1),
})

/** Parsed `StreamCastParams`. */
export type StreamCastParams = z.infer<typeof StreamCastParamsSchema>

/**
 * `GET /hypervisor/:id/cast` — stream the asciinema .cast file for one
 * Machine's pty. Response body is `application/x-asciinema-cast+json`;
 * route framework treats this as an opaque streaming response, so the
 * response schema is `z.unknown()` (the bytes are not Zod-validated).
 */
export const StreamCastRoute = defineRoute({
  method: 'GET',
  path: '/hypervisor/:id/cast',
  response: z.unknown(),
  description: CoreString.oneLine`
    Stream the asciinema cast file recorded for one Machine's pty. Body is
    \`application/x-asciinema-cast+json\`; opaque to the route framework.
    Consumed by admin's /admin/recordings/:id/cast viewer.
  `,
})
